import { useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from '@mui/material'
import { toast } from 'react-toastify'

import { GetApiErrorMessage, SaveFile } from 'helpers'
import { UploadSmartPayCsv as UploadSmartPayCsvApi } from 'services/RestClient'
import { SmartPayProcessingResult } from 'types'

import { FileDropzone, ProcessingResults } from './ui'

interface IProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const UploadSmartPayCsv = ({ open, onClose, onSuccess }: IProps) => {
  const [processing, setProcessing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [processingResult, setProcessingResult] = useState<SmartPayProcessingResult | null>(null)

  const handleFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only CSV files are allowed')
      return
    }

    setSelectedFile(file)
    setProcessingResult(null)
    toast.success(`File "${file.name}" selected. Click "Process" to continue.`)
  }

  const handleProcess = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first')
      return
    }

    setProcessing(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await UploadSmartPayCsvApi(formData)

      if (response.data) {
        setProcessingResult(response.data)
        toast.success(
          `Processing completed: ${response.data.matchedCount} matched, ${response.data.mismatchedCount} mismatched, ${response.data.notFoundCount} not found`,
        )
        onSuccess?.()
      }
    } catch (error: any) {
      console.error('Processing error:', error)
      console.error('Error response data:', error?.response?.data)

      const errorMessage = GetApiErrorMessage(error)
      toast.error(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setProcessingResult(null)
    onClose()
  }

  const downloadOutputCsv = () => {
    if (!processingResult?.outputCsv) return

    const fileName = `SmartPay_Processing_Result_${new Date().toISOString().split('T')[0]}.csv`
    SaveFile(processingResult.outputCsv, fileName)
  }

  const handleProcessAnother = () => {
    setProcessingResult(null)
    setSelectedFile(null)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        Upload & Process SmartPay CSV
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {processing && <LinearProgress sx={{ mb: 2 }} />}

        {!processingResult ? (
          <FileDropzone
            selectedFile={selectedFile}
            onFileSelect={handleFileSelect}
            disabled={processing}
          />
        ) : (
          <ProcessingResults
            result={processingResult}
            onDownloadCsv={downloadOutputCsv}
            onProcessAnother={handleProcessAnother}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color='inherit'>
          Close
        </Button>
        {!processingResult && (
          <Button
            onClick={handleProcess}
            variant='contained'
            color='primary'
            disabled={!selectedFile || processing}>
            {processing ? 'Processing...' : 'Process File'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
